/**
 * Net settlements for the Board pack: the loan book at the end of the pack month, and how it moved
 * over the month.
 *
 * THE SOURCE IS THE COMMISSION STATEMENTS, NOT SALESTREKKER. Matt Spears (Evoke, 7 Oct 2026)
 * defined net settlements as the net loan book balance. VALUTTEN already serves that figure: the
 * dashboard's Loan Book line (getLineOfTrailGrowth), month by month from the lender statements.
 * The pack asks that same callable with the dashboard's own scope, so the two can never disagree,
 * and no second, unreconciled copy of the book is computed here. Month keys are the statement
 * month, as the dashboard labels them.
 *
 * A MONTH WITHOUT STATEMENTS IS NEVER $0. The callable returns 0 for a month with no statement
 * upload, so 0 means "not loaded", not an empty book.
 *
 * A PART-LOADED MONTH IS NOT A COLLAPSE. When only some of a month's statements are in, the book
 * reads as a fraction of the month before (Evoke, Aug 2026: 53 loans paid commission against 1,186
 * in Jul, because one statement file of the month was loaded). Showing that as a fall of hundreds
 * of millions would be a wrong number on a board page, so a month whose book or loan count is below
 * INCOMPLETE_RATIO of the month before is named as incomplete and not shown. The same test runs the
 * other way: when the month before reads below INCOMPLETE_RATIO of this month, the month before is
 * the part-loaded one (Evoke's Sep pack against that Aug), so the closing book stands and the change
 * is withheld instead of printing a rise of hundreds of millions.
 */

/**
 * WHICH FIGURE IS THE HEADLINE. Matt has not yet said whether net settlements means the closing
 * balance or the change over the period; both are built. Once he answers, this is the one line to
 * change: 'closing' leads with the month-end book and shows the change beneath, 'change' the reverse.
 */
export const NET_SETTLEMENTS_HEADLINE: 'closing' | 'change' = 'closing';

/**
 * The headline figure named in plain words, for the Targets editor label and the panel's target
 * line, so the net settlements target can never be described as one figure while graded against
 * the other.
 */
export const NET_SETTLEMENTS_HEADLINE_LABEL: Record<'closing' | 'change', string> = {
  closing: 'closing loan book',
  change: 'change in the loan book over the month',
};

export const netSettlementsHeadlineLabel = (): string => NET_SETTLEMENTS_HEADLINE_LABEL[NET_SETTLEMENTS_HEADLINE];

/** Below this share of the previous month's book or loan count, a month is read as part-loaded. */
export const INCOMPLETE_RATIO = 0.75;

/** One month of the dashboard's Loan Book series. `loanAmount` is integer cents. */
export interface BookPoint {
  /** 'YYYY-MM', the statement month. */
  yearMonth?: string;
  /** Loans that paid commission in the month. */
  lot: number;
  /** Loan book in cents. */
  loanAmount: number;
}

export interface BookFigure {
  month: string;
  bookCents: number;
  loans: number;
}

export type NetBook =
  | { status: 'not_loaded'; month: string }
  | { status: 'incomplete'; month: string; loans: number; previousLoans: number; previousMonth: string }
  | {
      status: 'ok';
      closing: BookFigure;
      /** The month before, or why the change cannot be stated. */
      opening: BookFigure | { month: string; reason: 'not_loaded' | 'incomplete' };
      /** Closing less opening; null when there is no opening to compare with. */
      change: { bookCents: number; loans: number } | null;
    };

function figure(series: readonly BookPoint[], month: string): BookFigure | null {
  const point = series.find((p) => p.yearMonth === month);
  // 0 is how the callable reports a month with no statement upload (or a failed month query).
  if (!point || !Number.isFinite(point.loanAmount) || point.loanAmount <= 0) return null;
  return { month, bookCents: Math.round(point.loanAmount), loans: Math.max(0, Math.round(point.lot || 0)) };
}

/**
 * Net settlements for `month` ('YYYY-MM') from the Loan Book series, which must cover `month` and
 * `previousMonth`. Pure, so the rules are tested without a network.
 */
export function buildNetBook(series: readonly BookPoint[], month: string, previousMonth: string): NetBook {
  const closing = figure(series, month);
  if (!closing) return { status: 'not_loaded', month };
  const opening = figure(series, previousMonth);
  if (!opening) return { status: 'ok', closing, opening: { month: previousMonth, reason: 'not_loaded' }, change: null };
  const partLoaded = closing.bookCents < opening.bookCents * INCOMPLETE_RATIO
    || (opening.loans > 0 && closing.loans < opening.loans * INCOMPLETE_RATIO);
  if (partLoaded) {
    return { status: 'incomplete', month, loans: closing.loans, previousLoans: opening.loans, previousMonth };
  }
  const openingPartLoaded = opening.bookCents < closing.bookCents * INCOMPLETE_RATIO
    || (closing.loans > 0 && opening.loans < closing.loans * INCOMPLETE_RATIO);
  if (openingPartLoaded) {
    return { status: 'ok', closing, opening: { month: previousMonth, reason: 'incomplete' }, change: null };
  }
  return {
    status: 'ok',
    closing,
    opening,
    change: { bookCents: closing.bookCents - opening.bookCents, loans: closing.loans - opening.loans },
  };
}

/**
 * The figure the net settlements target is compared against: the headline, in cents, or null when
 * the headline has no figure (statements not loaded, a part-loaded month, or a change that cannot
 * be stated). Null grades nothing; it is never read as zero.
 */
export function netSettlementsHeadlineCents(book: NetBook): number | null {
  if (book.status !== 'ok') return null;
  if (NET_SETTLEMENTS_HEADLINE === 'closing') return book.closing.bookCents;
  return book.change ? book.change.bookCents : null;
}
